import { FusePrimaryPalette, generateContrasts } from './generate-palette';

export const DEFAULT_PRIMARY_COLOR = '#017E84';

export const PRIMARY_COLOR_STORAGE_KEY = 'bizcare-primary-color';

export type StoredPrimaryColor = {
    primaryHex: string;
    cssVars: Record<string, string>;
};

const FUSE_PRIMARY_PREFIX = '--fuse-primary';
const FUSE_ON_PRIMARY_PREFIX = '--fuse-on-primary';

/**
 * Build CSS custom properties for a primary palette (Fuse-compatible).
 */
export function buildPrimaryCssVars(
    palette: FusePrimaryPalette
): Record<string, string> {
    const onPrimary = generateContrasts(palette);
    const cssVars: Record<string, string> = {};

    Object.entries(palette).forEach(([shade, color]) => {
        if (shade === 'contrast') {
            cssVars['--fuse-primary-contrast'] = color;
            cssVars['--fuse-primary-contrast-rgb'] = hexToRgbCsv(color);
            return;
        }

        const suffix = shade === 'DEFAULT' ? '' : `-${shade}`;
        cssVars[`${FUSE_PRIMARY_PREFIX}${suffix}`] = color;
        cssVars[`${FUSE_PRIMARY_PREFIX}${suffix}-rgb`] = hexToRgbCsv(color);
    });

    Object.entries(onPrimary).forEach(([shade, color]) => {
        const suffix = shade === 'DEFAULT' ? '' : `-${shade}`;
        cssVars[`${FUSE_ON_PRIMARY_PREFIX}${suffix}`] = color;
        cssVars[`${FUSE_ON_PRIMARY_PREFIX}${suffix}-rgb`] = hexToRgbCsv(color);
    });

    cssVars['--bizcare-primary-light'] = palette['400'];
    cssVars['--bizcare-primary-mid'] = palette['500'];
    cssVars['--bizcare-primary-dark'] = palette['700'];
    cssVars['--bizcare-primary-darker'] = palette['800'];

    return cssVars;
}

export function applyPrimaryCssVars(
    cssVars: Record<string, string>,
    target: HTMLElement = document.body
): void {
    Object.entries(cssVars).forEach(([name, value]) => {
        target.style.setProperty(name, value);
    });
}

export function clearPrimaryCssVars(target: HTMLElement = document.body): void {
    const toRemove: string[] = [];

    for (let i = 0; i < target.style.length; i++) {
        const name = target.style.item(i);
        if (
            name.startsWith(FUSE_PRIMARY_PREFIX) ||
            name.startsWith(FUSE_ON_PRIMARY_PREFIX) ||
            name.startsWith('--bizcare-primary-')
        ) {
            toRemove.push(name);
        }
    }

    toRemove.forEach((name) => target.style.removeProperty(name));
}

export function updateThemeColorMeta(primaryHex: string): void {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) {
        meta.setAttribute('content', primaryHex);
    }
}

function hexToRgbCsv(hex: string): string {
    const [r, g, b] = hex
        .replace('#', '')
        .match(/.{1,2}/g)!
        .map((v) => parseInt(v, 16));
    return `${r},${g},${b}`;
}

/** Sync read for index.html bootstrap script (mirrors storage shape). */
export function readStoredPrimaryColor(): StoredPrimaryColor | null {
    try {
        const raw = localStorage.getItem(PRIMARY_COLOR_STORAGE_KEY);
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw) as StoredPrimaryColor;
        if (!parsed?.primaryHex || !parsed?.cssVars) {
            return null;
        }

        return parsed;
    } catch {
        return null;
    }
}

export function writeStoredPrimaryColor(data: StoredPrimaryColor): void {
    localStorage.setItem(PRIMARY_COLOR_STORAGE_KEY, JSON.stringify(data));
}

export function removeStoredPrimaryColor(): void {
    localStorage.removeItem(PRIMARY_COLOR_STORAGE_KEY);
}
